import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import Header from '../../../components/Header/Header';
import LandingAppStoreFooter from '../../apps/components/LandingAppStoreFooter';
import { eventAPI } from '../api/eventAPI';
import { useAuth } from '../../auth/hooks/useAuth';
import './EventVaultPage.css';

export default function EventVaultPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const isCertAdmin = user?.role === 'certadmin' || user?.role === 'admin';

  // Access code input & validation state
  const initialCode = searchParams.get('code') || sessionStorage.getItem('ivori_event_code') || '';
  const [accessCodeInput, setAccessCodeInput] = useState(initialCode);
  const [activeAccessCode, setActiveAccessCode] = useState(initialCode);
  const [eventData, setEventData] = useState(null);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [toastMessage, setToastMessage] = useState('');

  // Gallery filters & search
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  // Admin Modal
  const [showAdminModal, setShowAdminModal] = useState(false);
  const [newEventTitle, setNewEventTitle] = useState('');
  const [newEventCode, setNewEventCode] = useState('');
  const [newEventLocation, setNewEventLocation] = useState('Oxford Union, UK');
  const [newEventDate, setNewEventDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [newEventDesc, setNewEventDesc] = useState('');
  const [creatingEvent, setCreatingEvent] = useState(false);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 4000);
  };

  // Automatically attempt unlock if code provided in URL or session
  useEffect(() => {
    if (initialCode && !isUnlocked) {
      handleUnlock(initialCode);
    }
  }, []);

  const handleUnlock = async (codeToVerify) => {
    const code = (codeToVerify || accessCodeInput).trim().toUpperCase();
    if (!code) {
      setErrorMessage('Please enter an event access code.');
      return;
    }

    setVerifying(true);
    setErrorMessage('');

    try {
      const event = await eventAPI.verifyAccessCode(code);
      setEventData(event);
      setIsUnlocked(true);
      setActiveAccessCode(code);
      sessionStorage.setItem('ivori_event_code', code);
      setSearchParams({ code });
      showToast(`Welcome to ${event.title}!`);
    } catch (err) {
      setErrorMessage(err.message || 'Invalid or expired event access code.');
      setIsUnlocked(false);
    } finally {
      setVerifying(false);
    }
  };

  const handleLock = () => {
    setIsUnlocked(false);
    setEventData(null);
    sessionStorage.removeItem('ivori_event_code');
    setSearchParams({});
    setAccessCodeInput('');
  };

  // Filtered media items
  const filteredMedia = useMemo(() => {
    if (!eventData?.media) return [];
    return eventData.media.filter((item) => {
      const matchesCategory =
        selectedCategory === 'All' || item.category === selectedCategory;
      const matchesSearch =
        !searchQuery ||
        item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.caption && item.caption.toLowerCase().includes(searchQuery.toLowerCase()));
      return matchesCategory && matchesSearch;
    });
  }, [eventData, selectedCategory, searchQuery]);

  // Single file download trigger
  const handleDownloadSingle = (mediaItem) => {
    const link = document.createElement('a');
    link.href = mediaItem.media_url;
    link.download = `${mediaItem.title.replace(/\s+/g, '_')}.jpg`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast(`Downloading: ${mediaItem.title}`);
  };

  // Bulk download simulation/trigger
  const handleDownloadAll = async () => {
    if (!eventData?.media?.length) return;
    setIsDownloadingAll(true);
    showToast(`Preparing download package for ${eventData.media.length} items...`);

    try {
      // Trigger download for each item with slight delay
      for (let i = 0; i < Math.min(eventData.media.length, 10); i++) {
        const item = eventData.media[i];
        const link = document.createElement('a');
        link.href = item.media_url;
        link.download = `${item.title.replace(/\s+/g, '_')}.jpg`;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        await new Promise((r) => setTimeout(r, 400));
      }
      showToast('All event assets downloaded successfully.');
    } catch (err) {
      showToast('Download completed.');
    } finally {
      setIsDownloadingAll(false);
    }
  };

  // Keyboard navigation for Lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedMedia) return;
      if (e.key === 'Escape') setSelectedMedia(null);
      if (e.key === 'ArrowRight') {
        const currentIndex = filteredMedia.findIndex((m) => m.id === selectedMedia.id);
        if (currentIndex < filteredMedia.length - 1) {
          setSelectedMedia(filteredMedia[currentIndex + 1]);
        }
      }
      if (e.key === 'ArrowLeft') {
        const currentIndex = filteredMedia.findIndex((m) => m.id === selectedMedia.id);
        if (currentIndex > 0) {
          setSelectedMedia(filteredMedia[currentIndex - 1]);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedMedia, filteredMedia]);

  const handleCreateEventSubmit = async (e) => {
    e.preventDefault();
    setCreatingEvent(true);
    try {
      await eventAPI.createEvent({
        title: newEventTitle,
        access_code: newEventCode,
        location: newEventLocation,
        event_date: newEventDate,
        description: newEventDesc
      });
      showToast('Event created successfully!');
      setShowAdminModal(false);
      setAccessCodeInput(newEventCode);
      handleUnlock(newEventCode);
    } catch (err) {
      showToast(err.message || 'Failed to create event.');
    } finally {
      setCreatingEvent(false);
    }
  };

  return (
    <div className="event-vault-root">
      <Header />

      {toastMessage && <div className="event-toast">{toastMessage}</div>}

      <main className="event-vault-main">
        {!isUnlocked ? (
          /* GATED ACCESS SCREEN */
          <section className="event-gate-hero">
            <h1 className="event-gate-title">Enter Event Access Code</h1>

            <form
              className="event-gate-form"
              onSubmit={(e) => {
                e.preventDefault();
                handleUnlock();
              }}
            >
              <div className="gate-input-wrapper">
                <input
                  type="text"
                  className="gate-input"
                  placeholder="Enter access code..."
                  value={accessCodeInput}
                  onChange={(e) => {
                    setAccessCodeInput(e.target.value.toUpperCase());
                    setErrorMessage('');
                  }}
                  autoFocus
                />
                <button
                  type="submit"
                  className="gate-submit-btn"
                  disabled={verifying || !accessCodeInput.trim()}
                >
                  {verifying ? 'Unlocking...' : 'Unlock Gallery'}
                </button>
              </div>

              {errorMessage && <div className="gate-error-message">{errorMessage}</div>}
            </form>
          </section>
        ) : (
          /* UNLOCKED EVENT SHAREPOINT HUB */
          <section className="event-hub-container">
            {/* Event Header Banner */}
            <div className="event-banner-card">
              <div className="event-banner-content">
                <div className="event-banner-topline">
                  <span className="event-status-pill">
                    <span className="live-dot" /> Unlocked Event Vault
                  </span>
                  <span className="event-code-badge">CODE: {eventData?.access_code}</span>
                  {isCertAdmin && (
                    <Link to="/cert-portal" className="cert-portal-link-btn">
                      Cert Admin Portal &rarr;
                    </Link>
                  )}
                </div>

                <h1 className="event-hub-title">{eventData?.title}</h1>
                <p className="event-hub-desc">{eventData?.description}</p>

                <div className="event-meta-strip">
                  {eventData?.event_date && (
                    <div className="event-meta-item">
                      <span className="meta-icon">📅</span>
                      <span>{eventData.event_date}</span>
                    </div>
                  )}
                  {eventData?.location && (
                    <div className="event-meta-item">
                      <span className="meta-icon">📍</span>
                      <span>{eventData.location}</span>
                    </div>
                  )}
                  <div className="event-meta-item">
                    <span className="meta-icon">👥</span>
                    <span>{eventData?.attendee_count || 0} Registered Delegates</span>
                  </div>
                  <div className="event-meta-item">
                    <span className="meta-icon">🖼️</span>
                    <span>{eventData?.media?.length || 0} Assets</span>
                  </div>
                </div>
              </div>

              <div className="event-banner-actions">
                <button
                  className="btn-download-all"
                  onClick={handleDownloadAll}
                  disabled={isDownloadingAll || !eventData?.media?.length}
                >
                  {isDownloadingAll ? 'Bundling Download...' : 'Download Album (ZIP)'}
                </button>
                <button className="btn-lock-vault" onClick={handleLock} title="Exit album">
                  Lock & Exit
                </button>
              </div>
            </div>

            {/* Filter & Search Bar */}
            <div className="event-toolbar">
              <div className="event-category-pills">
                <button
                  className={`cat-pill ${selectedCategory === 'All' ? 'active' : ''}`}
                  onClick={() => setSelectedCategory('All')}
                >
                  All ({eventData?.media?.length || 0})
                </button>
                {(eventData?.categories || []).map((cat) => (
                  <button
                    key={cat}
                    className={`cat-pill ${selectedCategory === cat ? 'active' : ''}`}
                    onClick={() => setSelectedCategory(cat)}
                  >
                    {cat}
                  </button>
                ))}
              </div>

              <div className="event-search-box">
                <input
                  type="text"
                  placeholder="Search assets by keyword..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button className="clear-search-btn" onClick={() => setSearchQuery('')}>
                    &times;
                  </button>
                )}
              </div>
            </div>

            {/* Media Gallery Grid */}
            {filteredMedia.length === 0 ? (
              <div className="event-empty-state">
                <div className="empty-icon">🔍</div>
                <h3>No media found matching your filter</h3>
                <p>Try selecting a different category or clearing your search term.</p>
                <button
                  className="btn-reset-filter"
                  onClick={() => {
                    setSelectedCategory('All');
                    setSearchQuery('');
                  }}
                >
                  Reset Filters
                </button>
              </div>
            ) : (
              <div className="event-media-grid">
                {filteredMedia.map((item, idx) => (
                  <div
                    key={item.id || idx}
                    className="event-media-card"
                    onClick={() => setSelectedMedia(item)}
                  >
                    <div className="media-image-container">
                      <img
                        src={item.thumbnail_url || item.media_url}
                        alt={item.title}
                        loading="lazy"
                      />
                      <div className="media-card-badge">{item.category}</div>
                      <div className="media-card-hover-overlay">
                        <span className="hover-zoom-icon">🔍 Click to Preview</span>
                        <button
                          className="hover-download-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadSingle(item);
                          }}
                          title="Download high-resolution image"
                        >
                          ⬇ Download
                        </button>
                      </div>
                    </div>
                    <div className="media-card-info">
                      <div className="media-card-title">{item.title}</div>
                      {item.caption && <div className="media-card-caption">{item.caption}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        )}

        {/* LIGHTBOX MODAL */}
        {selectedMedia && (
          <div className="event-lightbox-backdrop" onClick={() => setSelectedMedia(null)}>
            <div className="event-lightbox-modal" onClick={(e) => e.stopPropagation()}>
              <button
                className="lightbox-close-btn"
                onClick={() => setSelectedMedia(null)}
                aria-label="Close"
              >
                &times;
              </button>

              <div className="lightbox-image-wrap">
                <img src={selectedMedia.media_url} alt={selectedMedia.title} />
              </div>

              <div className="lightbox-footer">
                <div className="lightbox-details">
                  <span className="lightbox-tag">{selectedMedia.category}</span>
                  <h2 className="lightbox-title">{selectedMedia.title}</h2>
                  {selectedMedia.caption && (
                    <p className="lightbox-caption">{selectedMedia.caption}</p>
                  )}
                </div>

                <div className="lightbox-actions">
                  <button
                    className="lightbox-download-btn"
                    onClick={() => handleDownloadSingle(selectedMedia)}
                  >
                    Download Full Resolution (JPG)
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ADMIN EVENT CREATION MODAL */}
        {showAdminModal && (
          <div className="event-admin-modal-backdrop" onClick={() => setShowAdminModal(false)}>
            <div className="event-admin-modal" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close-btn" onClick={() => setShowAdminModal(false)}>
                &times;
              </button>
              <h2>Create New Event Media Vault</h2>
              <form onSubmit={handleCreateEventSubmit}>
                <div className="admin-form-group">
                  <label>Event Title</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Ivori Cambridge Medical Immersion 2026"
                    value={newEventTitle}
                    onChange={(e) => setNewEventTitle(e.target.value)}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Attendee Access Code</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. CAMBRIDGE-MED-2026"
                    value={newEventCode}
                    onChange={(e) => setNewEventCode(e.target.value.toUpperCase())}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Location</label>
                  <input
                    type="text"
                    value={newEventLocation}
                    onChange={(e) => setNewEventLocation(e.target.value)}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Event Date</label>
                  <input
                    type="date"
                    value={newEventDate}
                    onChange={(e) => setNewEventDate(e.target.value)}
                  />
                </div>
                <div className="admin-form-group">
                  <label>Description</label>
                  <textarea
                    rows={3}
                    placeholder="Recap and details for delegates..."
                    value={newEventDesc}
                    onChange={(e) => setNewEventDesc(e.target.value)}
                  />
                </div>
                <button type="submit" className="admin-save-btn" disabled={creatingEvent}>
                  {creatingEvent ? 'Creating...' : 'Create & Launch Vault'}
                </button>
              </form>
            </div>
          </div>
        )}
      </main>

      <LandingAppStoreFooter />
    </div>
  );
}
